import { Mapper } from '@/core/mapper/base-mapper';
import { I18n } from '@aws-amplify/core';
import { GetDataExtractionResponseDTO } from '@/core/dto/data-extraction/get-data-extraction';
import { CombinedExtractionData } from '@/types';
import { ProcessingStatusType } from '@/types/DataExtraction';
import {
	REPORT_NAMES_MAP,
	TOOL_LABELS_MAP,
	ACTION_PLAN_COLUMNS_MAP,
	REPORT_ACRONYMS,
	REPORT_TYPES,
	REPORT_ACRONYM_KEYS
} from '@/utils/constants';

export class GetDataExtractionMapper implements Mapper<CombinedExtractionData> {
	constructor(private readonly data: GetDataExtractionResponseDTO) {}

	private getReportType(acronym: string): string {
		const acronymToTypeMap = {
			[REPORT_ACRONYMS.BERA]: REPORT_TYPES.BERA,
			[REPORT_ACRONYMS.SERA]: REPORT_TYPES.SERA
		} as const;

		return acronymToTypeMap[acronym as keyof typeof acronymToTypeMap] || REPORT_TYPES.CUSTOM;
	}

	toDomain(): CombinedExtractionData {
		return {
			id: this.data.id,
			name: this.data.extraction_name,
			author: this.data.author.name,
			content: this.data.content,
			createdAt: this.data.created_at,
			organization_name: this.data.organization_name,
			company_name: this.data.company_name,
			sector_name: this.data.sector_name,
			line_name: this.data.line_name,
			workstation_name: this.data.workstation_name,
			startDate: this.data.start_date,
			endDate: this.data.end_date,
			extractionType: this.data.extraction_type as 'file' | 'hierarchy',
			updatedAt: this.data.updated_at?.toString(),
			currentStepId: this.data.current_step?.id,
			status: this.data.status as ProcessingStatusType,
			originalStatus: this.data.status,
			dataExtractionReportsActionPlanColumns: this.data.data_extraction_reports_action_plan_columns
				? [
						{
							id: '',
							dataExtractionId: this.data.id,
							columns: this.data.data_extraction_reports_action_plan_columns.columns,
							formatted_columns: this.data.data_extraction_reports_action_plan_columns.columns?.map(
								(column) => I18n.get(ACTION_PLAN_COLUMNS_MAP[column] || column)
							)
						}
				  ]
				: undefined,
			data_extraction_tools: this.data.data_extraction_tools?.map((tool) => ({
				id: tool.id || tool.ergonomic_tool_id,
				ergonomic_tool_id: tool.ergonomic_tool_id,
				name: tool.name,
				label: I18n.get(TOOL_LABELS_MAP[tool.name] || tool.name)
			})),
			custom_report: this.data.custom_report
				? {
						name: I18n.get(REPORT_NAMES_MAP[this.data.custom_report.name] || this.data.custom_report.name),
						acronym: this.data.custom_report.acronym,
						report_type: this.getReportType(this.data.custom_report.acronym)
				  }
				: undefined,
			data_extraction_action_plan_origins: this.data.data_extraction_action_plan_origins
				? this.data.data_extraction_action_plan_origins.map((origin) => ({
						custom_report_id: origin.custom_report_id,
						custom_report_name: I18n.get(
							REPORT_ACRONYM_KEYS[origin.custom_report_name] || origin.custom_report_name
						),
						id: origin.id
				  }))
				: undefined,
			organization_id: this.data.organization_id,
			company_id: this.data.company_id,
			sector_id: this.data.sector_id,
			line_id: this.data.line_id,
			workstation_id: this.data.workstation_id,
			start_date: this.data.start_date,
			end_date: this.data.end_date,
			custom_report_id: this.data.custom_report_id,
			has_action_plan_extraction: this.data.has_action_plan_extraction,
			extraction_name: this.data.extraction_name,
			current_step: this.data.current_step
				? {
						...this.data.current_step,
						sequence: this.data.current_step.sequence || 1
				  }
				: undefined,
			data_extraction_reports: this.data.data_extraction_reports,
			data_extraction_reports_action_plan_columns: this.data.data_extraction_reports_action_plan_columns
		};
	}
}
